import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import './ConfirmDialog.css';
import { useTranslation, Trans } from 'react-i18next';

/**
 * The clinic's own confirmation, transcribed from the old application.
 *
 * `window.confirm` cannot do what this is asked to do: it has no icon, no
 * severity, no figures, no warning box, no place to type a reason, and no way
 * to make somebody type a word before a destructive action goes through. The
 * original built one dialog with all of that and used it twenty-five times,
 * from "Ștergi rolul?" to "Folosești avansul pacientului?".
 *
 * Two details that matter and are easy to get wrong, both kept:
 *
 * Cancelling must reach the caller. Something awaiting an answer would hang
 * for ever on Escape or a click on the backdrop, so closing calls `onClose`.
 *
 * Confirming must NOT call it. The dialog closes with the raw closer, because
 * `onClose` means "gave up" — call it while the user is confirming and a
 * caller waiting for the typed reason receives "cancelled" before the text
 * arrives, and every reason comes back empty.
 */

export interface ConfirmAsk {
  title: string;
  body?: string;
  /** normal | warn | danger — sets the colour and the default icon. */
  kind?: 'normal' | 'warn' | 'danger';
  icon?: string;
  /** Figures worth their own line rather than being buried in the sentence. */
  rows?: Array<{ l: string; v: string; c?: string }>;
  /** A red box under the body: what this will cost, said plainly. */
  warn?: string;
  /** Asks for a text — this is what replaces `window.prompt`. */
  input?: {
    label?: string;
    placeholder?: string;
    value?: string;
    required?: boolean;
    minLen?: number;
    type?: string;
    hint?: string;
  };
  /** Make them type this word before the action is allowed through. */
  confirmText?: string;
  yesLabel?: string;
  cancelLabel?: string;
  onYes?: (value?: string) => void;
  /** Called when they give up — Escape, backdrop, or Cancel. */
  onClose?: () => void;
}

const ConfirmContext = createContext<(ask: ConfirmAsk) => void>(() => {});

/** `const confirmAsk = useConfirm()` — then `confirmAsk({ title, onYes })`. */
export function useConfirm() {
  return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [ask, setAsk] = useState<ConfirmAsk | null>(null);
  // Counts openings, and keys the dialog by it. Each question therefore gets
  // a fresh component, so whatever was typed into the last one is gone
  // without an effect having to clear it.
  const [seq, setSeq] = useState(0);
  const confirmAsk = useCallback((next: ConfirmAsk) => {
    setAsk(next);
    setSeq((n) => n + 1);
  }, []);

  return (
    <ConfirmContext.Provider value={confirmAsk}>
      {children}
      <ConfirmDialog key={seq} ask={ask} onClose={() => setAsk(null)} />
    </ConfirmContext.Provider>
  );
}

export function ConfirmDialog({
  ask,
  onClose: closeRaw,
}: {
  ask: ConfirmAsk | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [typed, setTyped] = useState(ask?.input?.value ?? '');
  /**
   * The confirmation word has its own state.
   *
   * It used to share `typed` with the free-text input, so a dialog asking for
   * both — a reason, and the word typed out to prove you mean it — bound two
   * fields to one value: typing in either overwrote the other, and whichever
   * was typed last was what the caller received as the reason. A patient
   * deleted with a reason of "ȘTERGE" is how this was found.
   */
  const [confirmed, setConfirmed] = useState('');

  const giveUp = useCallback(() => {
    const f = ask?.onClose;
    closeRaw();
    if (f) f();
  }, [ask, closeRaw]);

  useEffect(() => {
    if (!ask) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') giveUp();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ask, giveUp]);

  if (!ask) return null;

  const kind = ask.kind ?? 'normal';
  const needType = ask.confirmText ?? null;
  const inputCfg = ask.input ?? null;

  const ready =
    !needType || confirmed.trim().toLowerCase() === String(needType).trim().toLowerCase();
  const inputOk = !inputCfg || !inputCfg.required || typed.trim().length >= (inputCfg.minLen ?? 1);
  const can = ready && inputOk;

  // Closes with the raw closer on purpose — see the note at the top.
  const go = () => {
    if (!can) return;
    const f = ask.onYes;
    const v = typed;
    closeRaw();
    if (f) f(inputCfg ? v.trim() : undefined);
  };

  return (
    <>
      <div className="cfd__scrim" onClick={giveUp} />
      <div
        className={`cfd cfd--${kind}`}
        role="dialog"
        aria-modal="true"
        aria-label={ask.title}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && can) go();
        }}
      >
        <header className="cfd__head">
          <span className="cfd__icon" aria-hidden>
            {ask.icon ?? (kind === 'danger' ? '⚠️' : kind === 'warn' ? '❗' : '❓')}
          </span>
          <h2 className="cfd__title">{ask.title}</h2>
        </header>

        <div className="cfd__body">
          {ask.body ? <p className="cfd__text">{ask.body}</p> : null}

          {/* Figures that matter, on their own lines rather than crammed
              into the sentence. */}
          {ask.rows && ask.rows.length > 0 ? (
            <div className="cfd__rows">
              {ask.rows.map((r) => (
                <div key={r.l} className="cfd__row">
                  <span>{r.l}</span>
                  <strong style={r.c ? { color: r.c } : undefined}>{r.v}</strong>
                </div>
              ))}
            </div>
          ) : null}

          {ask.warn ? <p className="cfd__warn">{ask.warn}</p> : null}

          {inputCfg ? (
            <div className="cfd__field">
              {inputCfg.label ? <label htmlFor="cfd-input">{inputCfg.label}</label> : null}
              <input
                id="cfd-input"
                autoFocus
                type={inputCfg.type ?? 'text'}
                placeholder={inputCfg.placeholder ?? ''}
                className={inputOk ? 'is-ok' : ''}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
              {inputCfg.hint ? <small>{inputCfg.hint}</small> : null}
            </div>
          ) : null}

          {needType ? (
            <div className="cfd__field">
              <label htmlFor="cfd-type">
                <Trans
                  i18nKey="common:scrieCaSaConfirmi"
                  components={[<b />]}
                  values={{ needType }}
                />
              </label>
              <input
                id="cfd-type"
                // Focus the reason when there is one: it is asked first, and
                // two fields both claiming focus is a race.
                autoFocus={!inputCfg}
                placeholder={needType}
                className={ready ? 'is-ok' : ''}
                value={confirmed}
                onChange={(e) => setConfirmed(e.target.value)}
              />
            </div>
          ) : null}
        </div>

        <footer className="cfd__actions">
          <button type="button" className="cfd__cancel" onClick={giveUp}>
            {ask.cancelLabel ?? t('anuleaza')}
          </button>
          <button
            type="button"
            className="cfd__go"
            disabled={!can}
            autoFocus={!needType && !inputCfg}
            onClick={go}
          >
            {ask.yesLabel ?? t('confirma')}
          </button>
        </footer>
      </div>
    </>
  );
}
